import { ApiError, fetcher, type ApiEnvelope } from "@/lib/api";
import { getApiBaseUrl } from "@/lib/api-base-url";
import {
  mapSessionApiResponse,
  type SessionApiResponse,
} from "@/features/quiz/api";
import type { QuizPublic, QuizSession } from "@/features/quiz/types";
import type { LearnProManualMethod } from "@/features/learn/pro/api";

/**
 * The Skill Test and the readiness check on the client (`LN-TST01`, docs/35 §6,
 * docs/36 §4, docs/07 "Skill Test").
 *
 * Everything about **this** learner (status, start, result, share switch,
 * readiness) goes through `fetcher` with the session. The public share card is
 * read on the server with a plain `fetch` and no credentials, like the shared
 * Playground snippet, so the page and its Open Graph image can be rendered for
 * a stranger's link preview.
 */

export type SkillBand = "advanced" | "proficient" | "working" | "foundation" | "not_yet";

export type SkillSections = { concepts: number; debug: number; build: number };

/**
 * `LN-CRT01`: what the Skill Certificate and the retake pack cost this viewer
 * (docs/35 §7). `currency` is who is paying (ADR-0067): `null` for a stranger,
 * who is shown both prices.
 */
export type SkillOffer = {
  onSale: boolean;
  currency: "USD" | "BDT" | null;
  certificate: {
    usd: number;
    bdt: number;
    listUsd: number;
    listBdt: number;
    discountPercent: number;
    discountReason: "track_owner" | "learn_pro" | null;
  };
  retake: { usd: number; bdt: number };
  payment: { cardCheckout: boolean; manualMethods: LearnProManualMethod[] };
};

export type SkillTestStatus = {
  trackSlug: string;
  open: boolean;
  testVersion: number | null;
  durationMinutes: number;
  sections: Array<{ key: keyof SkillSections; items: number; weight: number }>;
  certificateLine: number;
  /** `LN-CRT01`; absent from an API that predates it. */
  offer?: SkillOffer;
  viewer: {
    emailVerified: boolean;
    freeAvailable: boolean;
    purchasedRemaining: number;
    proCreditAvailable: boolean;
    nextEligibleAt: string | null;
    canStart: boolean;
    blockedReason: "gap" | "email_not_verified" | "no_credit" | null;
    openAttemptId: string | null;
    openAttemptSitting: boolean;
    best: { attemptId: string; skillScore: number; skillBand: SkillBand } | null;
    attemptsUsed: number;
    /** `LN-CRT01`: the best live certificate on this track. */
    certificate?: { certificateId: string; skillScore: number; skillBand: string } | null;
    /** A paid certificate is waiting for a score of 600 or more. */
    issuePending?: boolean;
    /** A passing, non-withdrawn score is held on this track. */
    holdsPassing?: boolean;
    /** The Learn Pro yearly credit can issue on the held score now. */
    canClaimPro?: boolean;
  } | null;
};

export type SkillAttemptPhase = "starting" | "sitting" | "grading" | "scored" | "failed";

export type SkillAttemptResult = {
  attemptId: string;
  trackSlug: string;
  track: { title_en: string; title_bn: string | null };
  kind: "free" | "purchased" | "pro_yearly";
  attemptNumber: number;
  testVersion: number;
  phase: SkillAttemptPhase;
  startedAt: string;
  scoredAt: string | null;
  skillScore: number | null;
  skillBand: SkillBand | null;
  skillSections: SkillSections | null;
  certificateLine: number;
  isBest: boolean;
  withdrawn: boolean;
  shareEnabled: boolean;
  nextEligibleAt: string | null;
};

export type SkillShare =
  | {
      attemptId: string;
      withdrawn: false;
      name: string;
      trackSlug: string;
      track: { title_en: string; title_bn: string | null };
      skillScore: number;
      skillBand: SkillBand;
      scoredAt: string;
      testVersion: number;
    }
  | {
      attemptId: string;
      withdrawn: true;
      trackSlug: string;
      track: { title_en: string; title_bn: string | null };
    };

/**
 * `fetcher` throws on an error envelope, so this only narrows the type; the
 * throw is for an envelope that says `success: false` with a 2xx status.
 */
function dataOf<T>(res: ApiEnvelope<T>): T {
  if (!res.success) throw new ApiError(res.error.message, 400, res.error.code, undefined, res.error.details);
  return res.data;
}

/** The `learn` message key for a band, so the two band lists cannot drift. */
export const BAND_MESSAGE_KEY: Record<SkillBand, "bandAdvanced" | "bandProficient" | "bandWorking" | "bandFoundation" | "bandNotYet"> = {
  advanced: "bandAdvanced",
  proficient: "bandProficient",
  working: "bandWorking",
  foundation: "bandFoundation",
  not_yet: "bandNotYet",
};

function isSkillBand(band: string | null | undefined): band is SkillBand {
  return (
    band === "advanced" ||
    band === "proficient" ||
    band === "working" ||
    band === "foundation" ||
    band === "not_yet"
  );
}

/** The band's message key, or `null` for anything the API did not send as a band. */
export function bandMessageKey(band: string | null | undefined): (typeof BAND_MESSAGE_KEY)[SkillBand] | null {
  return isSkillBand(band) ? BAND_MESSAGE_KEY[band] : null;
}

export async function fetchSkillTestStatus(trackSlug: string): Promise<SkillTestStatus> {
  const res = await fetcher<SkillTestStatus>(`/v1/skill-tests/${encodeURIComponent(trackSlug)}`);
  return dataOf(res);
}

/**
 * The Skill Test's public half, read on the server with no credentials
 * (`LN-LCH01`): the sections, the clock and the certificate line, which are the
 * same for every visitor, so the intro page has a real body in its HTML. The
 * route answers `viewer: null` to a stranger; the viewer's own state is the
 * same route read in the browser. Cached 60 s like the rest of Learn; `null`
 * on any failure, and the page then renders as it did before.
 */
export async function fetchSkillTestStatusOnServer(trackSlug: string): Promise<SkillTestStatus | null> {
  try {
    const res = await fetch(`${getApiBaseUrl()}/v1/skill-tests/${encodeURIComponent(trackSlug)}`, {
      next: { revalidate: 60 },
    });
    if (!res.ok) return null;
    const json = (await res.json()) as { success?: boolean; data?: SkillTestStatus };
    return json.success && json.data ? { ...json.data, viewer: null } : null;
  } catch {
    return null;
  }
}

export type SkillTestStarted = {
  attemptId: string;
  kind: SkillAttemptResult["kind"];
  attemptNumber: number;
  testVersion: number;
  isResume: boolean;
  quiz: QuizPublic;
  session: QuizSession;
};

export async function startSkillTest(
  trackSlug: string,
  useProCredit = false,
): Promise<SkillTestStarted> {
  const res = await fetcher<{
    attemptId: string;
    kind: SkillAttemptResult["kind"];
    attemptNumber: number;
    testVersion: number;
    isResume: boolean;
    quiz: QuizPublic;
    player: SessionApiResponse;
  }>(`/v1/skill-tests/${encodeURIComponent(trackSlug)}/start`, {
    method: "POST",
    body: JSON.stringify(useProCredit ? { useProCredit: true } : {}),
  });
  const { player, quiz, ...rest } = dataOf(res);
  return { ...rest, quiz, session: mapSessionApiResponse(quiz._id, player) };
}

export async function fetchSkillAttempt(attemptId: string): Promise<SkillAttemptResult> {
  const res = await fetcher<SkillAttemptResult>(`/v1/skill-tests/attempts/${encodeURIComponent(attemptId)}`);
  return dataOf(res);
}

export async function setSkillShare(attemptId: string, enabled: boolean): Promise<SkillAttemptResult> {
  const res = await fetcher<SkillAttemptResult>(
    `/v1/skill-tests/attempts/${encodeURIComponent(attemptId)}/share`,
    { method: "PATCH", body: JSON.stringify({ enabled }) },
  );
  return dataOf(res);
}

export type ReadinessQuestion = {
  id: string;
  text_en: string;
  text_bn: string | null;
  options: Array<{ id: string; text_en: string; text_bn: string | null }>;
};

export type ReadinessStart = {
  checkId: string;
  durationSeconds: number;
  expiresAt: string;
  questions: ReadinessQuestion[];
};

export type ReadinessResult = {
  ready: boolean;
  reread: Array<{ moduleId: string; title_en: string; title_bn: string | null; href: string | null }>;
};

export async function startReadinessCheck(trackSlug: string): Promise<ReadinessStart> {
  const res = await fetcher<ReadinessStart>(
    `/v1/learn/tracks/${encodeURIComponent(trackSlug)}/readiness`,
    { method: "POST", body: JSON.stringify({}) },
  );
  return dataOf(res);
}

export async function submitReadinessCheck(
  trackSlug: string,
  checkId: string,
  answers: Array<{ questionId: string; optionId: string }>,
): Promise<ReadinessResult> {
  const res = await fetcher<ReadinessResult>(
    `/v1/learn/tracks/${encodeURIComponent(trackSlug)}/readiness/${encodeURIComponent(checkId)}/submit`,
    { method: "POST", body: JSON.stringify({ answers }) },
  );
  return dataOf(res);
}

/**
 * The public share card, read on the server. `null` covers "switched off",
 * "never existed" and "unreachable" alike: the page and its image must not tell
 * a stranger which one it was.
 */
export async function fetchSkillShareOnServer(attemptId: string): Promise<SkillShare | null> {
  if (!/^[0-9a-fA-F]{24}$/.test(attemptId)) return null;
  try {
    // No cache: a learner who switches sharing off, or an integrity decision
    // that withdraws the score, must take effect on the next view.
    const res = await fetch(`${getApiBaseUrl()}/v1/skill-tests/share/${attemptId}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const json = (await res.json()) as { success?: boolean; data?: SkillShare };
    return json.success && json.data ? json.data : null;
  } catch {
    return null;
  }
}

// ── The certificate and the retake pack (`LN-CRT01`, docs/35 §7) ─────────────

export type SkillOrderProduct = "certificate" | "retake";

export type SkillOrder = {
  orderId: string;
  orderNumber: string;
  status: "pending" | "partially_paid" | "paid" | "cancelled" | "refunded";
  product: SkillOrderProduct;
  trackSlug: string;
  title: string;
  currency: "USD" | "BDT";
  subtotal: number;
  discount: number;
  total: number;
  discountPercent: number;
  discountReason: "track_owner" | "learn_pro" | null;
  paymentMethod: "lemonsqueezy" | "manual";
  paymentNote: string | null;
  createdAt: string;
};

export type SkillOrderCreated = {
  order: SkillOrder;
  checkoutUrl: string | null;
  manualMethods: LearnProManualMethod[] | null;
};

export async function createSkillOrder(
  trackSlug: string,
  product: SkillOrderProduct,
  locale: "en" | "bn",
): Promise<SkillOrderCreated> {
  const res = await fetcher<SkillOrderCreated>(`/v1/skill-tests/${encodeURIComponent(trackSlug)}/orders`, {
    method: "POST",
    body: JSON.stringify({ product, locale }),
  });
  return dataOf(res);
}

/** The bKash or Nagad transaction id, on the course order's own proof route. */
export async function sendSkillOrderProof(orderId: string, transactionId: string): Promise<void> {
  const res = await fetcher<unknown>(`/v1/orders/${encodeURIComponent(orderId)}/payment-proof`, {
    method: "PATCH",
    body: JSON.stringify({ paymentNote: transactionId }),
  });
  dataOf(res);
}

/** An order's state, for the buy page's return from the card checkout. */
export async function fetchSkillOrderStatus(orderId: string): Promise<SkillOrder["status"]> {
  const res = await fetcher<{ order: { status: SkillOrder["status"] } }>(`/v1/orders/${encodeURIComponent(orderId)}`);
  return dataOf(res).order.status;
}

/** The Learn Pro yearly credit issuing on a passing score already held. */
export async function claimProCertificate(trackSlug: string): Promise<{ claimed: true; certificateId: string | null }> {
  const res = await fetcher<{ claimed: true; certificateId: string | null }>(
    `/v1/skill-tests/${encodeURIComponent(trackSlug)}/certificate/claim`,
    { method: "POST", body: JSON.stringify({}) },
  );
  return dataOf(res);
}

// ── The skill profile: the Skills section of /u/[username] ────────────────────

export type SkillProfileEntry = {
  trackSlug: string;
  track: { title_en: string; title_bn: string | null };
  skillScore: number;
  skillBand: string;
  testVersion: number | null;
  issuedAt: string;
  certificateId: string;
};

/** Read on the server with no credentials; `[]` for hidden, none or unreachable alike. */
export async function fetchSkillProfileOnServer(username: string): Promise<SkillProfileEntry[]> {
  try {
    const res = await fetch(`${getApiBaseUrl()}/v1/users/${encodeURIComponent(username)}/skills`, {
      next: { revalidate: 60 },
    });
    if (!res.ok) return [];
    const json = (await res.json()) as { success?: boolean; data?: { skills?: SkillProfileEntry[] } };
    return json.success && Array.isArray(json.data?.skills) ? json.data.skills : [];
  } catch {
    return [];
  }
}

export type MySkillProfile = { hidden: boolean; username: string | null; skills: SkillProfileEntry[] };

export async function fetchMySkillProfile(): Promise<MySkillProfile> {
  const res = await fetcher<MySkillProfile>("/v1/me/skill-profile");
  return dataOf(res);
}

export async function setSkillProfileHidden(hidden: boolean): Promise<MySkillProfile> {
  const res = await fetcher<MySkillProfile>("/v1/me/skill-profile", {
    method: "PATCH",
    body: JSON.stringify({ hidden }),
  });
  return dataOf(res);
}
